if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        // Desktop breakpoint: 750px
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Use the <ul> as the scrollable container (if it exists)
        this.sliderContainer =
          this.elements.thumbnails.querySelector('ul') || this.elements.thumbnails;

        // ---- NEW: prevent first-load auto scrolls ----
        this._initialized = false;
        requestAnimationFrame(() => { this._initialized = true; });
        // ---------------------------------------------

        // Listen for slide changes on the viewer
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        // Attach click events for each thumbnail button
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
        });

        // Attach slider button event listeners for next/prev scrolling
        const sliderButtons = this.elements.thumbnails.querySelectorAll('.slider-button');
        if (sliderButtons.length && this.sliderContainer) {
          sliderButtons.forEach((button) => {
            button.addEventListener('click', () => {
              const isNext = button.classList.contains('slider-button--next');
              const direction = isNext ? 1 : -1;
              const scrollStep = 100; // Adjust this value based on your thumbnail height
              if (this.mql.matches) {
                // Desktop: vertical scrolling
                this.sliderContainer.scrollBy({
                  top: direction * scrollStep,
                  behavior: 'smooth'
                });
              } else {
                // Mobile: horizontal scrolling
                this.sliderContainer.scrollBy({
                  left: direction * scrollStep,
                  behavior: 'smooth'
                });
              }
            });
          });
        }

        // If using a thumbnail layout on desktop, remove extra semantic roles
        if (
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        // Setup thumb observer on desktop to animate thumbs in/out based on visibility
        if (this.mql.matches && this.sliderContainer) {
          this.setupThumbObserver();
        }
      }

      setupThumbObserver() {
        // Create an IntersectionObserver to monitor the visibility of each thumbnail (<li>)
        this.thumbObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // When at least 50% of the thumb is visible, consider it "visible"
              if (entry.intersectionRatio >= 0.5) {
                entry.target.classList.add('thumb-visible');
                entry.target.classList.remove('thumb-hidden');
              } else {
                entry.target.classList.add('thumb-hidden');
                entry.target.classList.remove('thumb-visible');
              }
            });
          },
          {
            root: this.sliderContainer,
            threshold: 0.5
          }
        );

        // Observe each thumbnail item (<li>)
        this.sliderContainer.querySelectorAll('li').forEach((thumb) => {
          this.thumbObserver.observe(thumb);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            if (this.mql.matches && this.sliderContainer) {
              activeMedia.parentElement.scrollTo({
                top: activeMedia.offsetTop,
                behavior: 'smooth'
              });
            } else {
              activeMedia.parentElement.scrollTo({
                left: activeMedia.offsetLeft,
                behavior: 'smooth'
              });
            }
          }

          // --- CHANGED: only scrollIntoView after init AND if out of view ---
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            if (this._initialized && !this.isInViewport(activeMedia)) {
              activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
          }
          // -------------------------------------------------------------------
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail || !this.sliderContainer) return;
        this.elements.thumbnails.querySelectorAll('button').forEach((element) =>
          element.removeAttribute('aria-current')
        );
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10))
          return;

        if (this.mql.matches) {
          this.sliderContainer.scrollTo({
            top: thumbnail.offsetTop,
            behavior: 'smooth'
          });
        } else {
          this.sliderContainer.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth'
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems && this.elements.viewer.sliderItems.forEach) {
          this.elements.viewer.sliderItems.forEach((slide) =>
            slide.setAttribute('role', 'presentation')
          );
        }
      }

      // ---- NEW: helper to check visibility in viewport ----
      isInViewport(el) {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const vw = window.innerWidth || document.documentElement.clientWidth;
        return r.top >= 0 && r.left >= 0 && r.bottom <= vh && r.right <= vw;
      }
      // -----------------------------------------------------
    }
  );
}
